'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetCompetitionCashProfitStandingsRow, GetCompetitionFairPnlStandingsRow, GetCompetitionStandingsRow } from '@morfi/backend-sdk-v2/client';
import { useAuth, useGetCompetition, useGetCompetitionCashProfitStandings, useGetCompetitionFairPnlStandings, useGetCompetitionMyStanding, useGetCompetitionStandings, useGetMyPayoutWallet, useSetMyPayoutWallet } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { CompetitionDetailPage, type CompetitionDetailStatus } from '@/components/social/competition/competition-detail-page';
import type { CompetitionStandingsStatus } from '@/components/social/competition/competition-standings';
import type { CompetitionViewerStanding, PayoutWalletState, TradingCompetitionSummary } from '@/components/social/competition/types';
import { useClientNowMs } from '@/components/social/competition/use-client-now';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { toCashProfitStandingRows, toCompetitionSummary, toFairPnlStandingRows, toStandingRows } from '@/lib/competitions/from-sdk';
import { logger } from '@/lib/telemetry/logger';

/**
 * Competition detail owner: the competition, its standings page, the viewer's
 * own standing and the payout wallet.
 *
 * `initialCompetition` is the row the route already read for metadata and the
 * not-found gate, so first paint renders from it instead of a skeleton while
 * the hook hydrates. Ranks come from the API; an absent rank stays `null` and
 * is never shown as a zero.
 */
export function CompetitionDetailData(t0) {
  const $ = _c(70);
  const {
    competitionId,
    initialCompetition,
    timeZone
  } = t0;
  const initialNowMs = useClientNowMs();
  const t = useTranslations("competitions.wallet");
  const {
    isAuthenticated,
    user
  } = useAuth();
  let t1;
  if ($[0] !== competitionId) {
    t1 = {
      competitionId
    };
    $[0] = competitionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const competitionQuery = useGetCompetition(t1);
  let t2;
  if ($[2] !== competitionQuery.data || $[3] !== initialCompetition) {
    t2 = competitionQuery.data === undefined ? initialCompetition : toCompetitionSummary(competitionQuery.data) ?? initialCompetition;
    $[2] = competitionQuery.data;
    $[3] = initialCompetition;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const competition = t2;
  let t3;
  if ($[5] !== competitionId) {
    t3 = {
      competitionId
    };
    $[5] = competitionId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = competition.rankingSource === "board";
  let t5;
  if ($[7] !== t4) {
    t5 = {
      enabled: t4
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const boardStandings = useGetCompetitionStandings(t3, t5);
  let t6;
  if ($[9] !== competitionId) {
    t6 = {
      competitionId
    };
    $[9] = competitionId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = competition.rankingSource === "fair_pnl";
  let t8;
  if ($[11] !== t7) {
    t8 = {
      enabled: t7
    };
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const fairPnlStandings = useGetCompetitionFairPnlStandings(t6, t8);
  let t9;
  if ($[13] !== competitionId) {
    t9 = {
      competitionId
    };
    $[13] = competitionId;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = competition.rankingSource === "cash_profit";
  let t11;
  if ($[15] !== t10) {
    t11 = {
      enabled: t10
    };
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const cashProfitStandings = useGetCompetitionCashProfitStandings(t9, t11);
  const boardRows = useLiveListData(boardStandings.queryKey);
  const fairPnlRows = useLiveListData(fairPnlStandings.queryKey);
  const cashProfitRows = useLiveListData(cashProfitStandings.queryKey);
  const standingsQuery = competition.rankingSource === "fair_pnl" ? fairPnlStandings : competition.rankingSource === "cash_profit" ? cashProfitStandings : boardStandings;
  let t12;
  if ($[17] !== boardRows || $[18] !== cashProfitRows || $[19] !== competition || $[20] !== fairPnlRows) {
    t12 = competition.rankingSource === "fair_pnl" ? toFairPnlStandingRows(fairPnlRows) : competition.rankingSource === "cash_profit" ? toCashProfitStandingRows(cashProfitRows) : toStandingRows(boardRows, competition);
    $[17] = boardRows;
    $[18] = cashProfitRows;
    $[19] = competition;
    $[20] = fairPnlRows;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const rows = t12;
  let t13;
  if ($[22] !== competitionId) {
    t13 = {
      competitionId
    };
    $[22] = competitionId;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isAuthenticated) {
    t14 = {
      enabled: isAuthenticated
    };
    $[24] = isAuthenticated;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const myStanding = useGetCompetitionMyStanding(t13, t14);
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {};
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== isAuthenticated) {
    t16 = {
      enabled: isAuthenticated
    };
    $[27] = isAuthenticated;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const payoutWallet = useGetMyPayoutWallet(t15, t16);
  const savePayoutWallet = useSetMyPayoutWallet();
  const t17 = competitionQuery.data !== undefined;
  let t18;
  if ($[29] !== competitionQuery.status || $[30] !== t17) {
    t18 = detailStatus(competitionQuery.status, t17);
    $[29] = competitionQuery.status;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== competition.phase || $[33] !== standingsQuery.isFetched || $[34] !== standingsQuery.status) {
    t19 = standingsStatus(standingsQuery.status, standingsQuery.isFetched, competition.phase);
    $[32] = competition.phase;
    $[33] = standingsQuery.isFetched;
    $[34] = standingsQuery.status;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = standingsQuery.hasNextPage === true;
  const t21 = standingsQuery.isFetchingNextPage === true;
  let t22;
  if ($[36] !== competition.rankingSource || $[37] !== competitionId || $[38] !== standingsQuery) {
    t22 = () => {
      standingsQuery.fetchNextPage().catch(error => {
        logger.warn("competition.standings.load_more_failed", {
          competitionId,
          rankingSource: competition.rankingSource,
          errorClass: error instanceof Error ? error.name : "unknown"
        });
      });
    };
    $[36] = competition.rankingSource;
    $[37] = competitionId;
    $[38] = standingsQuery;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== rows || $[41] !== standingsQuery.dataUpdatedAt || $[42] !== t19 || $[43] !== t20 || $[44] !== t21 || $[45] !== t22) {
    t23 = {
      status: t19,
      rows,
      updatedAtMs: standingsQuery.dataUpdatedAt,
      hasMore: t20,
      loadingMore: t21,
      onLoadMore: t22
    };
    $[40] = rows;
    $[41] = standingsQuery.dataUpdatedAt;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const t24 = user?.id ?? null;
  let t25;
  if ($[47] !== myStanding.data) {
    t25 = viewerStanding(myStanding.data);
    $[47] = myStanding.data;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const t26 = payoutWallet.data?.address ?? null;
  let t27;
  if ($[49] !== savePayoutWallet.isError || $[50] !== savePayoutWallet.isPending || $[51] !== t || $[52] !== t26) {
    t27 = walletState({
      address: t26,
      saving: savePayoutWallet.isPending,
      rejected: savePayoutWallet.isError,
      rejectionMessage: t("invalid")
    });
    $[49] = savePayoutWallet.isError;
    $[50] = savePayoutWallet.isPending;
    $[51] = t;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t24 || $[55] !== t25 || $[56] !== t27) {
    t28 = {
      userId: t24,
      standing: t25,
      payoutWallet: t27
    };
    $[54] = t24;
    $[55] = t25;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  let t30;
  if ($[58] !== savePayoutWallet) {
    t29 = (address, onSaved) => {
      savePayoutWallet.mutate({
        address
      }, {
        onSuccess: onSaved
      });
    };
    t30 = () => {
      savePayoutWallet.reset();
    };
    $[58] = savePayoutWallet;
    $[59] = t29;
    $[60] = t30;
  } else {
    t29 = $[59];
    t30 = $[60];
  }
  let t31;
  if ($[61] !== competition || $[62] !== initialNowMs || $[63] !== t18 || $[64] !== t23 || $[65] !== t28 || $[66] !== t29 || $[67] !== t30 || $[68] !== timeZone) {
    t31 = <CompetitionDetailPage status={t18} competition={competition} initialNowMs={initialNowMs} timeZone={timeZone} standings={t23} viewer={t28} onSaveWallet={t29} onWalletSheetClosed={t30} />;
    $[61] = competition;
    $[62] = initialNowMs;
    $[63] = t18;
    $[64] = t23;
    $[65] = t28;
    $[66] = t29;
    $[67] = t30;
    $[68] = timeZone;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  return t31;
}

/**
 * The route resolved the competition before rendering, so a pending hook keeps
 * showing that row; only a failed read falls back to the error state.
 */
function detailStatus(status: 'pending' | 'error' | 'success', hasRow: boolean): CompetitionDetailStatus {
  return status === 'error' && !hasRow ? 'error' : 'ready';
}

/**
 * Before the window opens nothing is ranked, so the board is `ready` with an
 * empty page — the delivered table renders its own upcoming state from the
 * competition's phase rather than a spinner that would never resolve.
 */
function standingsStatus(status: 'pending' | 'error' | 'success', isFetched: boolean, phase: TradingCompetitionSummary['phase']): CompetitionStandingsStatus {
  if (phase === 'upcoming') return 'ready';
  if (status === 'error') return 'error';
  return status === 'success' && isFetched ? 'ready' : 'loading';
}
function viewerStanding(row: {
  readonly rank: number | null;
  readonly value: string | null;
  readonly predictedWinningsD6: string | null;
} | undefined): CompetitionViewerStanding {
  if (row === undefined) return {
    rank: null,
    value: null,
    predictedWinningsD6: null
  };
  return {
    rank: row.rank,
    value: row.value,
    predictedWinningsD6: row.predictedWinningsD6
  };
}
function walletState(input: {
  address: string | null;
  saving: boolean;
  rejected: boolean;
  rejectionMessage: string;
}): PayoutWalletState {
  if (input.saving) return {
    status: 'saving',
    address: input.address,
    message: null
  };
  if (input.rejected) {
    return {
      status: 'error',
      address: input.address,
      message: input.rejectionMessage
    };
  }
  if (input.address !== null && input.address.length > 0) {
    return {
      status: 'saved',
      address: input.address,
      message: null
    };
  }
  return {
    status: 'none',
    address: null,
    message: null
  };
}